/**
 * CART DRAWER UPSELL — simplified version
 * -----------------------------------------
 * Shows products from the "Sale Special" collection as an upsell
 * inside the cart drawer, whenever the cart has at least one item.
 * No product tagging required.
 *
 * The % discount itself is handled by a Shopify Automatic Discount
 * (Admin > Discounts) scoped to the "Sale Special" collection —
 * this script only handles displaying + adding the upsell products.
 */

(function () {
  const UPSELL_COLLECTION_HANDLE = "sale-special"; // <-- your collection handle
  const UPSELL_CONTAINER_ID = "cart-upsell";
  const MAX_UPSELL_PRODUCTS = 4;
  const DISCOUNT_CALLOUT = "₹200 off when you add now"; // <-- edit this text as needed
  // Confirmed via: document.querySelector('cart-drawer-component').closest('[id^="shopify-section-"]')?.id
  const CART_SECTION_ID = "sections--17149729243343__header_section";

  let upsellProductsCache = null;

  async function fetchUpsellProducts() {
    if (upsellProductsCache) return upsellProductsCache;
    try {
      const res = await fetch(`/collections/${UPSELL_COLLECTION_HANDLE}/products.json?limit=${MAX_UPSELL_PRODUCTS}`);
      const data = await res.json();
      upsellProductsCache = data.products || [];
      return upsellProductsCache;
    } catch (e) {
      console.error("Upsell: failed to fetch collection", UPSELL_COLLECTION_HANDLE, e);
      return [];
    }
  }

  function formatMoney(amount, currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      // Fallback if currency code is somehow invalid
      return `${currencyCode} ${amount.toFixed(2)}`;
    }
  }

  function renderUpsellCard(product, currencyCode) {
    const variant = product.variants[0];
    const image = product.images && product.images[0] ? product.images[0].src : "";
    const price = parseFloat(variant.price);

    const card = document.createElement("div");
    card.className = "cart-upsell__card";
    card.innerHTML = `
      <a href="/products/${product.handle}" class="cart-upsell__link">
        <img src="${image}" alt="${product.title}" width="40" height="40" loading="lazy">
        <div class="cart-upsell__info">
          <p class="cart-upsell__title">${product.title}</p>
          <p class="cart-upsell__price">${formatMoney(price, currencyCode)}</p>
        </div>
      </a>
      <button type="button" class="cart-upsell__add-btn" data-variant-id="${variant.id}">
        Add
      </button>
    `;

    card.querySelector("button").addEventListener("click", async (e) => {
      const btn = e.currentTarget;
      btn.disabled = true;
      btn.textContent = "Adding…";
      await addToCart(variant.id);
      // No need to update btn text or manually re-render here —
      // addToCart() already triggers a full section refresh, which
      // replaces this button along with the rest of the drawer.
    });

    return card;
  }

  async function addToCart(variantId) {
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: CART_SECTION_ID,
          sections_url: window.location.pathname
        })
      });
      const data = await res.json();

      if (data.status) {
        console.error("Upsell DEBUG: cart/add returned an error", data);
        return;
      }

      console.log("Upsell DEBUG: add succeeded, sections keys:", Object.keys(data.sections || {}));
      console.log("Upsell DEBUG: expecting section id:", CART_SECTION_ID);
      console.log("Upsell DEBUG: matching section HTML present?", Boolean(data.sections?.[CART_SECTION_ID]));

      const cartItemsComponent = document.querySelector("cart-items-component");
      console.log("Upsell DEBUG: found cart-items-component in DOM?", Boolean(cartItemsComponent));
      if (cartItemsComponent) {
        console.log("Upsell DEBUG: its data-section-id =", cartItemsComponent.dataset.sectionId);
      }

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: "cart-upsell",
            data: {
              itemCount: data.item_count,
              variantId: variantId,
              sections: data.sections
            }
          }
        })
      );
      console.log("Upsell DEBUG: cart:update event dispatched");

      // Still re-render our own upsell block shortly after, since the
      // cart contents (and therefore which upsell items still make sense)
      // have changed.
      setTimeout(renderUpsellSection, 300);
    } catch (e) {
      console.error("Upsell: add to cart failed", e);
    }
  }

  async function renderUpsellSection() {
    const container = document.getElementById(UPSELL_CONTAINER_ID);
    if (!container) return;

    const cartRes = await fetch("/cart.js");
    const cart = await cartRes.json();

    if (cart.items.length === 0) {
      container.innerHTML = "";
      return;
    }

    const cartProductIds = new Set(cart.items.map((i) => i.product_id));
    const products = await fetchUpsellProducts();
    const productsToShow = products.filter((p) => !cartProductIds.has(p.id));

    if (productsToShow.length === 0) {
      container.innerHTML = "";
      return;
    }

    container.innerHTML = `
      <p class="cart-upsell__heading">You may like this</p>
      <p class="cart-upsell__subheading">${DISCOUNT_CALLOUT}</p>
    `;
    const list = document.createElement("div");
    list.className = "cart-upsell__list";

    for (const product of productsToShow) {
      list.appendChild(renderUpsellCard(product, cart.currency));
    }

    container.appendChild(list);
  }

  // Re-render on load (best effort — may be too early if the theme hasn't
  // finished hydrating the cart drawer yet)
  document.addEventListener("DOMContentLoaded", renderUpsellSection);

  // Most reliable trigger: populate the upsell the moment the user actually
  // opens the cart drawer, rather than relying on page-load timing that can
  // race against Horizon's own hydration of the drawer's contents.
  document.addEventListener("click", (e) => {
    const trigger = e.target.closest('[data-testid="cart-drawer-trigger"], [aria-label="Cart"]');
    if (trigger) {
      setTimeout(renderUpsellSection, 150);
    }
  });

  // MutationObserver: watches the cart items table for ANY change (add, remove,
  // quantity update, section re-render) regardless of which internal method
  // Horizon uses to perform that update. This is more reliable than trying to
  // intercept specific fetch URLs, since Horizon's Section Rendering API calls
  // don't necessarily match a predictable "/cart/..." pattern.
  let debounceTimer = null;
  function scheduleRerender() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(renderUpsellSection, 250);
  }

  let currentObserver = null;
  function startObserving() {
    const wrapper = document.querySelector(".cart-items__wrapper");
    if (!wrapper) {
      setTimeout(startObserving, 500);
      return;
    }
    if (currentObserver) currentObserver.disconnect();
    currentObserver = new MutationObserver((mutations) => {
      // Ignore mutations that originate from our own upsell container,
      // otherwise re-rendering it would re-trigger this observer forever.
      const isOwnMutation = mutations.every((m) => {
        const node = m.target.nodeType === 1 ? m.target : m.target.parentElement;
        return node && node.closest && node.closest(`#${UPSELL_CONTAINER_ID}`);
      });
      if (isOwnMutation) return;
      scheduleRerender();
    });
    currentObserver.observe(wrapper, { childList: true, subtree: true, characterData: true });
  }

  document.addEventListener("DOMContentLoaded", startObserving);
  // In case DOMContentLoaded already fired before this script ran
  if (document.readyState !== "loading") startObserving();

  // Extra safety net for changes NOT caused by our own Add button (e.g. a
  // quantity update inside the drawer). Deliberately does NOT intercept
  // fetch globally — that previously caught our own internal /cart.js
  // polling calls too, causing an infinite re-render loop.
})();